import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLES = [
  { key: "admin", label: "Admin" },
  { key: "sourcing_manager", label: "Sourcing Manager" },
  { key: "relationship_manager", label: "Relationship Manager" },
];

const roleLabel = (r) => ROLES.find((x) => x.key === r)?.label || r;

function UserDialog({ user, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    password: "",
    roles: user?.roles || [],
  });

  const toggleRole = (r) =>
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
    }));

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        const payload = { name: form.name, roles: form.roles };
        if (form.password) payload.password = form.password;
        return api.patch(`/admin/users/${user.id}`, payload);
      }
      return api.post("/admin/users", {
        email: form.email, name: form.name, password: form.password, roles: form.roles,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(isEdit ? "User updated" : "User created");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const valid =
    form.name.trim() &&
    form.roles.length > 0 &&
    (isEdit || (form.email.includes("@") && form.password.length >= 6));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${user.name}` : "Add team member"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input value={form.email} disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1" data-testid="user-email" />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1" data-testid="user-name" />
          </div>
          <div>
            <Label>{isEdit ? "Reset password (optional)" : "Password"}</Label>
            <Input type="password" value={form.password}
              placeholder={isEdit ? "Leave blank to keep current" : "Min 6 characters"}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1" data-testid="user-password" />
          </div>
          <div>
            <Label>Roles</Label>
            <div className="mt-2 space-y-2">
              {ROLES.map((r) => (
                <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.roles.includes(r.key)}
                    onChange={() => toggleRole(r.key)} data-testid={`user-role-${r.key}`} />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}
            className="bg-slate-900 hover:bg-slate-800" data-testid="user-save-btn">
            {isEdit ? "Save changes" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UserManagement() {
  const qc = useQueryClient();
  const [dialogUser, setDialogUser] = useState(undefined); // undefined=closed, null=create, obj=edit

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const toggleActive = useMutation({
    mutationFn: async (u) => api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div className="p-8 max-w-4xl" data-testid="user-management-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">Admin</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">User & Role Management</h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Onboard team members and assign roles. Users can hold multiple roles. Deactivated users
            cannot sign in.
          </p>
        </div>
        <Button onClick={() => setDialogUser(null)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add user
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="label-eyebrow !text-slate-600">Name</th>
              <th className="label-eyebrow !text-slate-600">Email</th>
              <th className="label-eyebrow !text-slate-600">Roles</th>
              <th className="label-eyebrow !text-slate-600">Active</th>
              <th className="label-eyebrow !text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">Loading…</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="text-sm font-semibold text-slate-900">{u.name}</td>
                <td className="text-sm text-slate-600 font-mono-num">{u.email}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(u.roles || []).map((r) => (
                      <Badge key={r} variant="outline" className="text-[10px] border-slate-200 text-slate-700">
                        {roleLabel(r)}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive.mutate(u)} data-testid={`user-active-${u.id}`} />
                    {u.is_active === false && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ backgroundColor: "#E6EAEE", color: "#5C7080" }}
                        data-testid={`user-inactive-pill-${u.id}`}
                      >
                        Inactive
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDialogUser(u)} data-testid={`user-edit-${u.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialogUser !== undefined && (
        <UserDialog user={dialogUser} onClose={() => setDialogUser(undefined)} />
      )}
    </div>
  );
}
